import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchSites, siteRemoved } from "../../redux/Slice/siteSlice";
import { errorMessage } from "../../services";
import { markProductSetup, markWelcome, withdrawSiteDeletion } from "../../services/sites";
import { useDebounced } from "../../utils/hooks";
import Pager from "../../components/Pager";
import { FilterButton, FilterChips, FilterField, FilterSheet } from "../../components/FilterSheet";
import { getFranchisees } from "../../services/franchisees";
import DeleteSiteModal from "./DeleteSiteModal";
import AddSiteModal from "./AddSiteModal";
import WhatsAppMessageModal from "./WhatsAppMessageModal";
import PayModal from "./PayModal";
import SiteTable from "./SiteTable";
import useSiteActions from "./useSiteActions";

const SUPER_FILTERS = ["All", "Product setup requested", "Deletion requested", "Domain needs linking", "Active", "Inactive", "Trial", "Paid", "Expired"];
const STORE_FILTERS = ["All", "Active", "Inactive", "Trial", "Paid", "Expired"];
const FILTER_TONES = { "Deletion requested": "red", "Domain needs linking": "amber", "Needs linking": "amber", Active: "green", Paid: "green", Trial: "amber", Inactive: "red", Expired: "red" };
const SORTS = [["newest", "Newest first"], ["oldest", "Oldest first"], ["name", "Name A–Z"], ["expiry", "Expiring soonest"]];

// Search, filter, sort and paging all happen on the server. `demo` (Super Admin) lists only ownezzy.com's demo
// stores; every other list leaves them out.
export default function SitesTab({ user, notify, setTab, demo = false }) {
  const dispatch = useDispatch();
  const { sites, total, page, pages, loading, error } = useSelector((state) => state.site);
  const isSuper = user.role === "superadmin";
  const isSupport = user.role === "support"; // every customer's website, to edit only
  const filters = isSuper || isSupport ? SUPER_FILTERS : STORE_FILTERS;
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState(() => {
    // A link can pick the filter, e.g. the "Deletion requested" alert: /admin/all-websites?filter=Deletion%20requested
    const fromLink = new URLSearchParams(window.location.search).get("filter");
    if (fromLink && filters.includes(fromLink)) return fromLink;
    try {
      const stored = sessionStorage.getItem("oz_sites_filter");
      if (stored) {
        sessionStorage.removeItem("oz_sites_filter");
        return stored;
      }
    } catch {}
    return "All";
  });
  const [sort, setSort] = useState("newest");
  const [pageNumber, setPageNumber] = useState(1);
  const [adding, setAdding] = useState(false);
  const [franchiseId, setFranchiseId] = useState(""); // Super Admin: only one franchise's websites
  const [franchises, setFranchises] = useState([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [message, setMessage] = useState(null); // { site, kind: "welcome" | "custom" }: the WhatsApp message being written
  const debouncedSearch = useDebounced(search);
  const canAdd = !demo && (isSuper || user.role === "subadmin");

  const load = useCallback(() => {
    dispatch(fetchSites({ search: debouncedSearch, filter, sort, franchiseId: franchiseId || undefined, demo: demo ? "only" : undefined, page: pageNumber, limit: 20 }));
  }, [dispatch, debouncedSearch, filter, sort, franchiseId, demo, pageNumber]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (isSuper) getFranchisees({ limit: 100 }).then(({ data }) => setFranchises(data.franchisees)).catch(() => setFranchises([]));
  }, [isSuper]);

  const actions = useSiteActions({ notify, reload: load });
  // Owners and partners ask for a deletion (a Super Admin deletes); staff cannot, and demo stores are never deleted
  const canDelete = !demo && user.role !== "staff" && !isSupport;
  const cancelDeletion = async (site) => {
    if (!window.confirm(`Cancel the request to delete ${site.name}?`)) return;
    try {
      const { data } = await withdrawSiteDeletion(site.id);
      notify(data.message);
      load();
    } catch (err) {
      notify(errorMessage(err));
    }
  };
  // Ownezzy's team: the free product setup is done (or open again)
  const productSetupDone = async (site, done) => {
    try {
      const { data } = await markProductSetup(site.id, done);
      notify(data.message);
      load();
    } catch (err) {
      notify(errorMessage(err), "error");
    }
  };
  // Super Admin: cross off the welcome message once it has reached the customer (or undo)
  const welcomeSent = async (site, sent) => {
    try {
      const { data } = await markWelcome(site.id, sent);
      notify(data.message);
      setMessage(null);
      load();
    } catch (err) {
      notify(errorMessage(err), "error");
    }
  };
  // Any change to the search box or filters starts again from page 1
  const change = (setter) => (e) => { setter(e.target.value); setPageNumber(1); };
  const pick = (setter) => (value) => { setter(value); setPageNumber(1); };
  const activeFilters = [filter !== "All", sort !== "newest", franchiseId].filter(Boolean).length;
  const resetFilters = () => { setFilter("All"); setSort("newest"); setFranchiseId(""); setPageNumber(1); };
  const franchiseSelect = (
    <select value={franchiseId} onChange={change(setFranchiseId)} aria-label="Filter by franchise">
      <option value="">All franchises</option>
      {franchises.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
    </select>
  );

  return (
    <>
      <div className="page-heading">
        <div>
          <p className="eyebrow">{demo ? "OWNEZZY.COM SAMPLES" : "STOREFRONT MANAGEMENT"}</p>
          <h1>{demo ? "Demo stores" : isSuper || isSupport ? "All websites" : "My websites"}</h1>
          <p className="subheading">
            {demo
              ? "The sample websites shown on ownezzy.com. They are not customer websites and are not counted in All websites or the Overview."
              : "Review plans, expiry dates, access status and edit any storefront."}
          </p>
        </div>
        {canAdd && <button className="primary-btn" onClick={() => setAdding(true)}>＋ Add website</button>}
      </div>

      <div className="panel table-panel">
        <div className="table-toolbar">
          <input value={search} onChange={change(setSearch)} placeholder="Search store, owner or domain…" />
          <FilterButton count={activeFilters} onClick={() => setFiltersOpen(true)} />
          <div className="toolbar-inline-filters">
            <select value={filter} onChange={change(setFilter)} aria-label="Filter websites">{filters.map((f) => <option key={f}>{f}</option>)}</select>
            <select value={sort} onChange={change(setSort)} aria-label="Sort websites">{SORTS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>
            {isSuper && franchises.length > 0 && franchiseSelect}
          </div>
          <span>{total} {demo ? "demo stores" : "websites"}</span>
        </div>
        <div className="toolbar-count">{total} {demo ? "demo stores" : "websites"}</div>
        {error && <div className="admin-error">{error}</div>}
        <SiteTable sites={sites} user={user} loading={loading} onToggle={actions.toggle} onPay={actions.openPay} onLinkDomain={actions.linkDomain} onDelete={canDelete ? setDeleting : undefined} onCancelDeletion={cancelDeletion}
          onProductSetup={(isSuper || isSupport) && !demo ? productSetupDone : undefined}
          {...(isSuper && !demo ? { onMessage: (site, kind) => setMessage({ site, kind }), onWelcomeSent: welcomeSent } : {})} />
        <Pager page={page} pages={pages} total={total} noun="websites" onPage={setPageNumber} />
      </div>

      {adding && (
        <AddSiteModal user={user} onClose={() => setAdding(false)} onNeedCustomer={() => setTab("Customers")}
          onCreated={(message) => { setAdding(false); notify(message); load(); }} />
      )}
      {filtersOpen && (
        <FilterSheet title="Filter websites" resultLabel={`Show ${total} website${total === 1 ? "" : "s"}`} onClose={() => setFiltersOpen(false)} onReset={resetFilters}>
          <FilterChips label="Status" value={filter} onChange={pick(setFilter)} options={filters.map((f) => ({ value: f, label: f, tone: FILTER_TONES[f] }))} />
          <FilterChips label="Sort by" value={sort} onChange={pick(setSort)} options={SORTS.map(([value, label]) => ({ value, label }))} />
          {isSuper && franchises.length > 0 && <FilterField label="Franchise">{franchiseSelect}</FilterField>}
        </FilterSheet>
      )}
      {deleting && (
        <DeleteSiteModal site={deleting} isSuper={isSuper} onClose={() => setDeleting(null)}
          onDone={(message, deleted) => { if (deleted) dispatch(siteRemoved(deleting.id)); setDeleting(null); notify(message); load(); }} />
      )}
      {message && <WhatsAppMessageModal site={message.site} kind={message.kind} notify={notify} onClose={() => setMessage(null)} onWelcomeSent={welcomeSent} />}
      {actions.payTarget && <PayModal site={actions.payTarget} isSuperAdmin={isSuper} onSubmit={actions.pay} onClose={actions.closePay} />}
    </>
  );
}
